/**
 * Keeps the web session alive while the app is open (XC-03 / AUTH-10).
 *
 * Shortly before the access token lapses it is renewed with the refresh token; only when renewal
 * is refused is the session ended — AuthContext then signs the user out to their own sign-in page
 * with a message. A malformed stored token ends the session instead of crashing the app (XC-04).
 * Requests that hit a dead session are renewed or ended the same way by the API client.
 */

import { jwtDecode } from "jwt-decode";
import { useEffect } from "react";

import { expireSession, renewSession } from "../api/client";
import { useAuth } from "./AuthContext";
import { getAccessToken } from "./tokenStore";

const CHECK_EVERY_MS = 30_000;
// Renew this long before expiry, so a request never races the lapse.
const RENEW_AHEAD_SECONDS = 120;

export function SessionExpiry() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    let stopped = false;

    const check = async () => {
      const token = getAccessToken();
      if (!token) return;
      let exp: number | undefined;
      try {
        exp = jwtDecode(token)?.exp;
      } catch {
        expireSession(); // a malformed stored token can never be valid
        return;
      }
      if (exp === undefined || exp - Date.now() / 1000 > RENEW_AHEAD_SECONDS) return;
      const outcome = await renewSession();
      // "offline": couldn't reach the server — keep the session and try again next tick.
      if (!stopped && outcome === "rejected") expireSession();
    };

    void check();
    const timer = setInterval(() => void check(), CHECK_EVERY_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [user]);

  return null;
}
